import React from 'react';

import { useTranslation } from '@/i18n';
import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { IconScout } from '@/components/ui/images/IconScout';
import Table from '@/components/ui/tables/Table';
import { useAttendanceConceptList } from '@/hooks/attendance/concept/useAttendanceConceptList';
import { useAttendanceConceptExport } from '@/hooks/attendance/concept/useAttendanceConceptExport';


export const AttendanceConceptListPage: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    hasRexIntegration,
    isSyncing,
    searchText,
    onCreate,
    onSync,
  } = useAttendanceConceptList();

  const { onExport, isExporting } = useAttendanceConceptExport({ searchText });

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="attendance-concept-list-page">
        <div className="flex justify-between items-center gap-4">
          <PermissionGuard action="create">
            {hasRexIntegration ? (
              <PrimaryButton
                iconLeft={<IconScout name="sync" />}
                onClick={onSync}
                isLoading={isSyncing}
                data-testid="sync-attendance-concept-button"
              >
                {isSyncing
                  ? translate('attendance:attendanceConcept.actions.syncing')
                  : translate('attendance:attendanceConcept.actions.sync')}
              </PrimaryButton>
            ) : (
              <PrimaryButton
                iconLeft={<IconScout name="plus" />}
                onClick={onCreate}
                data-testid="create-attendance-concept-button"
              >
                {translate('attendance:attendanceConcept.actions.create')}
              </PrimaryButton>
            )}
          </PermissionGuard>
          <PermissionGuard action="view" fallback="hidden">
            <ExportDropdown
              onExport={onExport}
              placement="right"
              isLoading={isExporting}
              availableFormats={['EXCEL']}
            />
          </PermissionGuard>
        </div>
        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          searchable
          onSearch={onSearch}
          onSortChange={onSortChange}
          serverSideSort
          initialSort={{ key: 'name', direction: 'asc' }}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          isLoading={loading}
          actions={actions}
          data-testid="attendance-concept-table"
        />
      </div>
    </PageLayout>
  );
};

export default AttendanceConceptListPage;
